import * as React from 'react';
import { Rectangle } from 'ark-common';
import { Vector2D } from '../../types/common';
import { RawPointerEvent, UIEventListeners } from '../../../../../common/src/ts/types/uievent';
import { isTouchEvent } from '../../../../../common/src/ts/utils/uievent';
import UIEventHandler from '../../components/app/UIEventHandler';
import {
  ComposerViewState,
  composerViewParamsFromPageSize,
  normalizeComposerPan,
} from '../reducers/view';

interface PageViewportProps {
  enableZoomAndPan: boolean;
  view: ComposerViewState;
  clientRect: Rectangle;
  contentWidth: number;
  contentHeight: number;
  onChangePan: (pan: Vector2D) => void;
}

enum PageViewportWorkMode {
  None,
  Dragging,
}

interface PageViewportWorkNone {
  mode: PageViewportWorkMode.None;
}

interface PageViewportWorkDragging {
  mode: PageViewportWorkMode.Dragging;
  oldPan: Vector2D;
  anchor: Vector2D;
}

interface PageViewportState {
  work: PageViewportWorkNone | PageViewportWorkDragging;
}

function getPointerCoordsFromEvent(ev: RawPointerEvent): Vector2D {
  if (isTouchEvent(ev)) {
    return {
      x: ev.touches[0].screenX,
      y: ev.touches[0].screenY,
    };
  }

  return { x: ev.screenX, y: ev.screenY };
}

class PageViewport extends React.Component<PageViewportProps, PageViewportState> {
  private uiEventListeners: UIEventListeners = {
    onPointerDown: (ev: RawPointerEvent) => {
      const coords = getPointerCoordsFromEvent(ev);
      this.setState({
        work: {
          mode: PageViewportWorkMode.Dragging,
          oldPan: this.props.view.pan,
          anchor: coords,
        },
      });
    },

    onPointerMove: (ev: RawPointerEvent) => {
      const { work } = this.state;
      switch (work.mode) {
        case PageViewportWorkMode.Dragging: {
          const pointer = getPointerCoordsFromEvent(ev);
          const nextPanBeforeClamping = {
            x: work.oldPan.x + (pointer.x - work.anchor.x),
            y: work.oldPan.y + (pointer.y - work.anchor.y),
          };
          const viewParams = composerViewParamsFromPageSize(
            this.props.clientRect,
            this.props.contentWidth,
            this.props.contentHeight,
          );
          const nextPan = normalizeComposerPan(
            viewParams,
            this.props.view.zoom,
            nextPanBeforeClamping,
          );
          this.props.onChangePan(nextPan);
          break;
        }
      }
    },

    onPointerUp: () => {
      const { work } = this.state;
      switch (work.mode) {
        case PageViewportWorkMode.Dragging: {
          this.setState({ work: { mode: PageViewportWorkMode.None } });
          break;
        }
      }
    },

    onGestureStart: () => { },
    onGestureChange: () => { },
    onGestureEnd: () => { },
  };

  public render() {
    const { zoom, pan } = this.props.view;
    const blockStyle: React.CSSProperties = {
      transformOrigin: 'left top',
      transform: `scale(${zoom})`,
      position: 'absolute',
      left: `${pan.x}px`,
      top: `${pan.y}px`,
    };

    return (
      <div>
        <div style={blockStyle}>
          {this.props.children}
        </div>
        {this.props.enableZoomAndPan ?
          <UIEventHandler uiEventListeners={this.uiEventListeners} /> : null}
      </div>
    );
  }

  public constructor(props: Readonly<PageViewportProps>) {
    super(props);
    this.state = { work: { mode: PageViewportWorkMode.None } };
  }
}

export default PageViewport;
